import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'usehooks-ts';
import { Button, Stack } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { every } from 'lodash';
import { NumericString } from '@utils/typescript';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import { ConfirmModal } from '@components/Base';
import { HealthClaimCostItem, HealthClaimCostItemStatuses } from '@models/HealthClaimCostItem';
import { selectHealthClaim } from '../selector';
import {
  benefitLimitsFetch,
  HealthClaimCostItemsFetchPayload,
  healthClaimCostItemsFetch,
  HealthClaimCostItemCreatePayload,
  healthClaimCostItemCreate,
  healthClaimCostItemUpdate,
  HealthClaimCostItemUpdatePayload,
  healthClaimCostItemDelete,
  HealthClaimCostItemsApprovePayload,
  healthClaimCostItemsApprove,
} from '../action';

import { CostItemFormModal } from './cost-item-form-modal';
import { FormValues } from './schema';

type TotalAmountsProps = {
  totalDeclaredAmount: NumericString;
  totalUncoveredExpensesAmount: NumericString;
  totalDeductibleCopayAmount: NumericString;
  totalOverLimitAmount: NumericString;
  totalCoveredAmount: NumericString;
};

const TotalAmounts = ({
  totalDeclaredAmount,
  totalUncoveredExpensesAmount,
  totalDeductibleCopayAmount,
  totalOverLimitAmount,
  totalCoveredAmount,
}: TotalAmountsProps) => {
  return (
    <Stack direction="horizontal" gap={5} className="mt-5">
      <Stack>
        <span>Total amount declared:</span>
        <h5>{Number(totalDeclaredAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total expenses not covered:</span>
        <h5>{Number(totalUncoveredExpensesAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total deductible or co-pay amount:</span>
        <h5>{Number(totalDeductibleCopayAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total over limit amount:</span>
        <h5>{Number(totalOverLimitAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total amount covered:</span>
        <h5>{Number(totalCoveredAmount).toFixed(2)}</h5>
      </Stack>
    </Stack>
  );
};

const EstimatedCostsSection = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedHealthClaimCostItem, setSelectedHealthClaimCostItem] = useState<HealthClaimCostItem | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { healthClaim, benefitLimits, healthClaimCostItems } = useSelector(selectHealthClaim);
  const totalDeclaredAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.declaredAmount),
    0,
  );
  const totalUncoveredExpensesAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.uncoveredExpensesAmount),
    0,
  );
  const totalDeductibleCopayAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.deductibleCopayAmount),
    0,
  );
  const totalOverLimitAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.overLimitAmount),
    0,
  );
  const totalCoveredAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.totalCoveredAmount),
    0,
  );

  const columns = useMemo(
    () =>
      [
        {
          name: 'Sub benfit',
          selector: (row) => (row.benefitLimit ? row.benefitLimit.subBenefit : row.customSubBenefit),
          width: '300px',
          wrap: true,
        },
        {
          name: 'Amount declared',
          selector: (row) => row.declaredAmount,
          width: '200px',
          wrap: true,
        },
        {
          name: 'Approved amount',
          selector: (row) => row.approvedAmount,
          width: '200px',
          wrap: true,
        },
        {
          name: 'Expenses not covered',
          selector: (row) => row.uncoveredExpensesAmount,
          width: '200px',
        },
        {
          name: 'Deductible or co-pay amount',
          selector: (row) => row.deductibleCopayAmount,
          width: '300px',
        },
        {
          name: 'Over limit amount',
          selector: (row) => row.overLimitAmount,
          width: '200px',
        },
        {
          name: 'Total amount covered',
          selector: (row) => row.totalCoveredAmount,
          width: '200px',
        },
        {
          name: 'Remark',
          selector: (row) => row.remark,
          width: '300px',
          wrap: true,
        },
        {
          name: 'Status',
          selector: (row) => t(`healthClaimCostItem.statuses.${row.status}`),
        },
        {
          name: 'Actions',
          cell: (row) => {
            if (row.status === HealthClaimCostItemStatuses.Approved) return;

            return (
              <Stack direction="horizontal" gap={2}>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setSelectedHealthClaimCostItem(row);
                    setShowFormModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPencil} className="fa-fw" />
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    setSelectedHealthClaimCostItem(row);
                    setShowConfirmModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} className="fa-fw" />
                </Button>
              </Stack>
            );
          },
        },
      ] as TableColumn<HealthClaimCostItem>[],
    [],
  );

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
      },
    },
  };

  useEffect(() => {
    if (!healthClaim?.benefitTemplateId) return;
    dispatch(benefitLimitsFetch({ benefitTemplateId: healthClaim.benefitTemplateId }));
  }, [healthClaim]);

  useEffect(() => {
    if (!healthClaim) return;
    const payload: HealthClaimCostItemsFetchPayload = { healthClaimId: healthClaim.id };
    dispatch(healthClaimCostItemsFetch(payload));
  }, [healthClaim]);

  return (
    <section>
      <div className="d-flex flex-row justify-content-between align-items-center mb-5">
        <h5>Estimated costs</h5>
        {every(healthClaimCostItems, (item) => item.status === HealthClaimCostItemStatuses.Pending) && (
          <Stack direction="horizontal" gap={2}>
            {healthClaimCostItems && healthClaimCostItems.length > 0 && (
              <Button variant="success" onClick={() => setShowConfirmModal(true)}>
                <FontAwesomeIcon icon={faCheck} className="fa-fw me-2" />
                Approve
              </Button>
            )}
            <Button onClick={() => setShowFormModal(true)}>
              <FontAwesomeIcon icon={faPlus} className="fa-fw me-2" />
              Add cost item
            </Button>
          </Stack>
        )}
      </div>

      <DataTable columns={columns} data={healthClaimCostItems ?? []} customStyles={customStyles} fixedHeader />

      <TotalAmounts
        totalDeclaredAmount={`${totalDeclaredAmount}`}
        totalUncoveredExpensesAmount={`${totalUncoveredExpensesAmount}`}
        totalDeductibleCopayAmount={`${totalDeductibleCopayAmount}`}
        totalOverLimitAmount={`${totalOverLimitAmount}`}
        totalCoveredAmount={`${totalCoveredAmount}`}
      />

      {healthClaim && (
        <>
          <CostItemFormModal
            benefitLimits={benefitLimits ?? []}
            healthClaimCostItem={selectedHealthClaimCostItem}
            show={showFormModal}
            onHide={() => {
              setSelectedHealthClaimCostItem(null);
              setShowFormModal(false);
            }}
            onSubmit={(values: FormValues) => {
              if (!selectedHealthClaimCostItem) {
                const payload: HealthClaimCostItemCreatePayload = {
                  ...values,
                  healthClaimId: healthClaim.id,
                  benefitLimitId: Number(values.benefitLimitId),
                };
                dispatch(healthClaimCostItemCreate(payload));
              } else {
                const payload: HealthClaimCostItemUpdatePayload = {
                  ...values,
                  id: selectedHealthClaimCostItem.id,
                  benefitLimitId: Number(values.benefitLimitId),
                };
                dispatch(healthClaimCostItemUpdate(payload));
              }

              setSelectedHealthClaimCostItem(null);
              setShowFormModal(false);
            }}
          />

          <ConfirmModal
            show={showConfirmModal}
            onHide={() => setShowConfirmModal(false)}
            onConfirm={() => {
              if (selectedHealthClaimCostItem) {
                dispatch(healthClaimCostItemDelete({ id: selectedHealthClaimCostItem?.id }));
                setShowConfirmModal(false);
                setSelectedHealthClaimCostItem(null);
              } else {
                const payload: HealthClaimCostItemsApprovePayload = { healthClaimId: healthClaim.id };
                dispatch(healthClaimCostItemsApprove(payload));
                setShowConfirmModal(false);
              }
            }}
          />
        </>
      )}
    </section>
  );
};

export { EstimatedCostsSection };
